import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, getCountFromServer, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { PageHeader, Card, StatCard, Spinner, TabButtons } from '../components/UI';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend,
  AreaChart, Area } from 'recharts';

const ACCENT = '#008751';
const GOLD = '#D4AF37';
const COLORS = ['#008751','#D4AF37','#3b82f6','#a855f7','#ef4444','#f97316','#06b6d4'];

const CHART_TOOLTIP = {
  contentStyle:{background:'#1A2332',border:'1px solid rgba(0,135,81,0.2)',
    borderRadius:10,fontSize:12,boxShadow:'0 8px 24px rgba(0,0,0,0.3)'},
  itemStyle:{color:'#fff'},
  labelStyle:{color:'rgba(255,255,255,0.5)',marginBottom:4},
};

const COUNTRIES = [
  {code:'NG',name:'Nigeria',flag:'🇳🇬'},
  {code:'US',name:'United States',flag:'🇺🇸'},
  {code:'GB',name:'United Kingdom',flag:'🇬🇧'},
  {code:'CA',name:'Canada',flag:'🇨🇦'},
  {code:'GH',name:'Ghana',flag:'🇬🇭'},
  {code:'ZA',name:'South Africa',flag:'🇿🇦'},
];

const AGE_GROUPS = ['junior','teen','adult','senior'];
const TIERS = ['free','student','premium','scholar','family'];

export default function Analytics() {
  const [countryData,  setCountryData]  = useState([]);
  const [ageData,      setAgeData]      = useState([]);
  const [genderData,   setGenderData]   = useState([]);
  const [tierHistory,  setTierHistory]  = useState([]);
  const [topUsers,     setTopUsers]     = useState([]);
  const [dailyUsage,   setDailyUsage]   = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [timeRange,    setTimeRange]    = useState('week');

  useEffect(() => { loadAnalytics(); }, []);

  async function loadAnalytics() {
    try {
      const usersCol = collection(db, 'users');

      // Country counts
      const countryCounts = await Promise.all(
        COUNTRIES.map(c =>
          getCountFromServer(query(usersCol, where('country','==',c.code)))
            .then(s => ({...c, count: s.data().count}))
        )
      );
      setCountryData(countryCounts.filter(c => c.count > 0)
        .sort((a,b) => b.count - a.count));

      // Age group counts
      const ageCounts = await Promise.all(
        AGE_GROUPS.map(a =>
          getCountFromServer(query(usersCol, where('ageGroup','==',a)))
            .then(s => ({name: a.charAt(0).toUpperCase()+a.slice(1), count: s.data().count}))
        )
      );
      setAgeData(ageCounts.filter(a => a.count > 0));

      // Gender counts
      const [maleSnap, femaleSnap, otherSnap] = await Promise.all([
        getCountFromServer(query(usersCol, where('gender','==','male'))),
        getCountFromServer(query(usersCol, where('gender','==','female'))),
        getCountFromServer(query(usersCol, where('gender','==','other'))),
      ]);
      const gData = [
        {name:'Male',   value: maleSnap.data().count,   color:'#3b82f6'},
        {name:'Female', value: femaleSnap.data().count, color:'#ec4899'},
        {name:'Other',  value: otherSnap.data().count,  color:'#6b7280'},
      ].filter(g => g.value > 0);
      setGenderData(gData);

      // Tier counts
      const tierCounts = await Promise.all(
        TIERS.map(t =>
          getCountFromServer(query(usersCol, where('subscriptionTier','==',t)))
            .then(s => ({tier:t, count: s.data().count}))
        )
      );
      setTierHistory(tierCounts);

      // Top users by messages
      try {
        const topSnap = await getDocs(query(usersCol,
          orderBy('totalQuestionsAsked','desc'), limit(10)));
        setTopUsers(topSnap.docs.map(d => ({id:d.id,...d.data()})));
      } catch(e) { console.log('Top users skipped:', e.message); }

      // Daily usage (last 7 days)
      try {
        const usageData = [];
        for (let i = 6; i >= 0; i--) {
          const dayStart = new Date(Date.now() - i*24*60*60*1000);
          dayStart.setHours(0,0,0,0);
          const dayEnd = new Date(dayStart.getTime() + 24*60*60*1000);
          const snap = await getCountFromServer(query(
            collection(db,'api_logs'),
            where('timestamp','>=',Timestamp.fromDate(dayStart)),
            where('timestamp','<', Timestamp.fromDate(dayEnd)),
          )).catch(() => ({data:()=>({count:0})}));
          usageData.push({
            day: dayStart.toLocaleDateString('en',{weekday:'short',month:'short',day:'numeric'}),
            messages: snap.data().count,
          });
        }
        setDailyUsage(usageData);
      } catch(e) { console.log('Daily usage skipped:', e.message); }

    } catch(e) { console.error('Analytics error:', e); }
    setLoading(false);
  }

  if (loading) return <div style={{padding:32}}><Spinner /></div>;

  const totalCountry = countryData.reduce((s,c) => s+c.count, 0) || 1;
  const totalUsers = tierHistory.reduce((s,t) => s+t.count, 0);
  const totalMessages = dailyUsage.reduce((s,d) => s+d.messages, 0);

  return (
    <div style={{padding:32}}>
      <PageHeader title="Analytics"
        subtitle="User demographics, engagement metrics, and usage insights" />

      {/* Overview Stats */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',
        gap:16,marginBottom:28}}>
        <StatCard label="Total Users" value={totalUsers} icon="👥"
          color={ACCENT} borderColor={ACCENT} />
        <StatCard label="Messages (7d)" value={totalMessages} icon="💬"
          color="#3b82f6" borderColor="#3b82f6" />
        <StatCard label="Countries" value={countryData.length} icon="🌍"
          color={GOLD} borderColor={GOLD} />
        <StatCard label="Top Country"
          value={countryData[0]?.flag + ' ' + (countryData[0]?.name || 'N/A')}
          icon="🏆" color="#a855f7" borderColor="#a855f7" />
      </div>

      {/* Daily Usage Chart */}
      <Card title="Daily Message Volume" style={{marginBottom:20}}>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={dailyUsage} margin={{top:5,right:10,left:-20,bottom:0}}>
            <defs>
              <linearGradient id="usageGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={ACCENT} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={ACCENT} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="day" tick={{fill:'rgba(255,255,255,0.35)',fontSize:11}}
              axisLine={{stroke:'rgba(255,255,255,0.06)'}} />
            <YAxis tick={{fill:'rgba(255,255,255,0.35)',fontSize:11}}
              axisLine={{stroke:'rgba(255,255,255,0.06)'}} />
            <Tooltip {...CHART_TOOLTIP} />
            <Area type="monotone" dataKey="messages" stroke={ACCENT}
              strokeWidth={2} fill="url(#usageGrad)"
              dot={{fill:ACCENT,r:4,strokeWidth:0}}
              activeDot={{r:6,fill:ACCENT,stroke:'#fff',strokeWidth:2}} />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      {/* Users by Country */}
      <Card title="Users by Country" style={{marginBottom:20}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:32,alignItems:'center'}}>
          <div>
            {countryData.map((c, i) => (
              <div key={c.code} style={{marginBottom:16}}>
                <div style={{display:'flex',justifyContent:'space-between',
                  marginBottom:6,alignItems:'center'}}>
                  <span style={{fontSize:14,color:'rgba(255,255,255,0.7)'}}>
                    {c.flag} {c.name}
                  </span>
                  <span style={{fontFamily:'DM Mono',fontSize:13,
                    color:ACCENT,fontWeight:600}}>
                    {c.count} ({((c.count/totalCountry)*100).toFixed(1)}%)
                  </span>
                </div>
                <div style={{height:8,background:'rgba(255,255,255,0.04)',
                  borderRadius:4,overflow:'hidden'}}>
                  <div style={{
                    height:'100%',borderRadius:4,
                    width:`${(c.count/totalCountry)*100}%`,
                    background: `linear-gradient(90deg, ${COLORS[i % COLORS.length]}, ${COLORS[i % COLORS.length]}88)`,
                    transition:'width 0.8s ease',
                  }} />
                </div>
              </div>
            ))}
            {countryData.length === 0 && (
              <p style={{color:'rgba(255,255,255,0.3)',fontSize:13}}>No country data yet</p>
            )}
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={countryData} dataKey="count" nameKey="name"
                innerRadius={65} outerRadius={105} paddingAngle={2} strokeWidth={0}>
                {countryData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip {...CHART_TOOLTIP}
                formatter={(v,n) => [v + ' users', n]} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Top Users + Age/Gender Row */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20,marginBottom:20}}>
        {/* Top Users by Messages */}
        <Card title="Top Users by Messages">
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {topUsers.length > 0 ? topUsers.slice(0,8).map((u, i) => (
              <div key={u.id} style={{display:'flex',alignItems:'center',gap:12,
                padding:'10px 12px',background: i < 3 ? 'rgba(212,175,55,0.04)' : 'transparent',
                borderRadius:10,border: i < 3 ? '1px solid rgba(212,175,55,0.08)' : '1px solid transparent'}}>
                <div style={{width:28,height:28,borderRadius:'50%',
                  background: i === 0 ? 'rgba(212,175,55,0.2)' : i === 1 ? 'rgba(192,192,192,0.2)' : i === 2 ? 'rgba(205,127,50,0.2)' : 'rgba(255,255,255,0.04)',
                  display:'flex',alignItems:'center',justifyContent:'center',
                  fontSize:12,fontWeight:800,color: i === 0 ? GOLD : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : 'rgba(255,255,255,0.3)',
                  fontFamily:'DM Mono',flexShrink:0}}>
                  {i + 1}
                </div>
                <div style={{flex:1,overflow:'hidden'}}>
                  <div style={{fontSize:13,fontWeight:600,color:'#fff',
                    whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                    {u.displayName || u.email?.split('@')[0] || 'Unknown'}
                  </div>
                </div>
                <span style={{fontFamily:'DM Mono',fontSize:13,fontWeight:600,
                  color:ACCENT}}>{u.totalQuestionsAsked || 0}</span>
              </div>
            )) : (
              <div style={{color:'rgba(255,255,255,0.3)',fontSize:13,padding:'20px 0',
                textAlign:'center'}}>No user data yet</div>
            )}
          </div>
        </Card>

        {/* Age Group Distribution */}
        <Card title="Age Group Distribution">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={ageData} margin={{top:0,right:0,left:-20,bottom:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="name" tick={{fill:'rgba(255,255,255,0.4)',fontSize:12}}
                axisLine={{stroke:'rgba(255,255,255,0.06)'}} />
              <YAxis tick={{fill:'rgba(255,255,255,0.4)',fontSize:12}}
                axisLine={{stroke:'rgba(255,255,255,0.06)'}} />
              <Tooltip {...CHART_TOOLTIP} />
              <Bar dataKey="count" fill={ACCENT} radius={[6,6,0,0]} fillOpacity={0.85} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Gender + Tier Row */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20,marginBottom:20}}>
        <Card title="Gender Breakdown">
          {genderData.length > 0 ? (
            <div style={{display:'flex',alignItems:'center',gap:24}}>
              <ResponsiveContainer width={160} height={160}>
                <PieChart>
                  <Pie data={genderData} innerRadius={45} outerRadius={70}
                    paddingAngle={3} dataKey="value" strokeWidth={0}>
                    {genderData.map((g,i) => (
                      <Cell key={i} fill={g.color} />
                    ))}
                  </Pie>
                  <Tooltip {...CHART_TOOLTIP} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{flex:1}}>
                {genderData.map((g,i) => (
                  <div key={i} style={{display:'flex',alignItems:'center',
                    justifyContent:'space-between',marginBottom:14,
                    padding:'8px 12px',background:'rgba(255,255,255,0.02)',borderRadius:8}}>
                    <div style={{display:'flex',alignItems:'center',gap:10}}>
                      <div style={{width:12,height:12,borderRadius:3,
                        background:g.color}} />
                      <span style={{fontSize:14,color:'rgba(255,255,255,0.6)'}}>{g.name}</span>
                    </div>
                    <span style={{fontFamily:'DM Mono',color:'#fff',fontSize:14,
                      fontWeight:600}}>{g.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p style={{color:'rgba(255,255,255,0.3)',fontSize:13,padding:'20px 0'}}>
              No gender data yet
            </p>
          )}
        </Card>

        {/* Tier Distribution Bar */}
        <Card title="Subscription Tier Distribution">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={tierHistory} margin={{top:0,right:20,left:-20,bottom:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="tier"
                tickFormatter={v => v.charAt(0).toUpperCase()+v.slice(1)}
                tick={{fill:'rgba(255,255,255,0.4)',fontSize:12}}
                axisLine={{stroke:'rgba(255,255,255,0.06)'}} />
              <YAxis tick={{fill:'rgba(255,255,255,0.4)',fontSize:12}}
                axisLine={{stroke:'rgba(255,255,255,0.06)'}} />
              <Tooltip {...CHART_TOOLTIP}
                formatter={(v,n) => [v+' users', n.charAt(0).toUpperCase()+n.slice(1)]} />
              <Bar dataKey="count" radius={[6,6,0,0]}>
                {tierHistory.map((entry,i) => {
                  const colors = {free:'#6b7280',student:'#008751',premium:'#3b82f6',
                    scholar:'#D4AF37',family:'#a855f7'};
                  return <Cell key={i} fill={colors[entry.tier]||'#6b7280'} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Category Breakdown Summary */}
      <Card title="Platform Summary">
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:16}}>
          {[
            {label:'Total Users', value:totalUsers, icon:'👥', color:ACCENT},
            {label:'Paid Users', value:tierHistory.filter(t=>t.tier!=='free').reduce((s,t)=>s+t.count,0),
              icon:'💎', color:GOLD},
            {label:'Free Users', value:tierHistory.find(t=>t.tier==='free')?.count||0,
              icon:'🆓', color:'#6b7280'},
            {label:'Countries Reached', value:countryData.length, icon:'🌍', color:'#3b82f6'},
            {label:'Messages (7d)', value:totalMessages, icon:'💬', color:'#a855f7'},
            {label:'Avg Messages/Day', value:Math.round(totalMessages/7), icon:'📊', color:'#f97316'},
          ].map(item => (
            <div key={item.label} style={{padding:'18px',background:'#0F1419',
              borderRadius:12,border:'1px solid rgba(255,255,255,0.04)',
              borderLeft:`3px solid ${item.color}`}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
                <span style={{fontSize:16}}>{item.icon}</span>
                <span style={{fontSize:11,color:'rgba(255,255,255,0.35)',
                  textTransform:'uppercase',letterSpacing:1}}>{item.label}</span>
              </div>
              <div style={{fontSize:22,fontWeight:800,fontFamily:'Syne',color:'#fff'}}>
                {item.value}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
